import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30000; const DEBOUNCE_MS = 400; const STALE_WAKE_MS = 20000;
const LiveCtx = createContext(null);

/** One SSE connection per tab, shared by every screen. Subscribers get (topic, data); "poll" means "reconcile: refetch current state".
 *  Reconnects with backoff; on reconnect / tab wake / online it triggers a reconcile so nothing missed stays stale; polls every 30s while disconnected. */
export function LiveProvider({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState("connecting"); // connecting | live | reconnecting | offline
  const subs = useRef(new Map()); const es = useRef(null); const timers = useRef({}); const lastId = useRef(null); const everOpen = useRef(false); const hiddenAt = useRef(0);
  const token = typeof window !== "undefined" ? localStorage.getItem("sigmacare_token") : null;
  const ctxKey = `${user?.id || "anon"}:${token ? token.slice(-12) : ""}`;

  const dispatch = (topic, data) => {
    subs.current.forEach((s, id) => {
      if (topic !== "poll" && !s.topics.has(topic)) return;
      if (topic === "poll") { s.cb("poll", {}); return; }
      const key = `${id}:${topic}`; s.pending[topic] = data; clearTimeout(timers.current[key]);
      timers.current[key] = setTimeout(() => { const d = s.pending[topic]; delete s.pending[topic]; s.cb(topic, d || {}); }, DEBOUNCE_MS); // coalesce bursts
    });
  };
  const reconcile = () => dispatch("poll", {});

  useEffect(() => {
    let closed = false, failures = 0, pollTimer = null, retry = null;
    const stopPoll = () => { if (pollTimer) { clearInterval(pollTimer); pollTimer = null; } };
    const startPoll = () => { if (!pollTimer) pollTimer = setInterval(reconcile, POLL_MS); };
    const connect = () => {
      if (closed) return;
      if (!window.EventSource) { setStatus("offline"); startPoll(); return; }
      const qs = [token ? `token=${encodeURIComponent(token)}` : "", lastId.current ? `last_id=${encodeURIComponent(lastId.current)}` : ""].filter(Boolean).join("&");
      const src = new EventSource(`${API}/events/stream${qs ? `?${qs}` : ""}`, { withCredentials: true }); es.current = src;
      src.addEventListener("ready", () => { failures = 0; setStatus("live"); stopPoll(); if (everOpen.current) reconcile(); everOpen.current = true; });
      src.addEventListener("reconcile", reconcile);
      src.addEventListener("close", () => { src.close(); setTimeout(connect, 800); }); // server asked us to reopen with the new session/permission context
      src.onmessage = () => {};
      const onAny = (e) => { lastId.current = e.lastEventId || lastId.current; let d = {}; try { d = JSON.parse(e.data).data || {}; } catch { d = {}; } dispatch(e.type, d); };
      ["queue", "appointment", "prescription", "order", "lab", "collection", "delivery", "notification", "payment", "finance", "access", "access.changed", "diagnostics", "report", "inventory", "physio", "enquiry", "doctor", "catalog", "corporate", "membership", "support", "referral", "admin_alert", "emr"].forEach((t) => src.addEventListener(t, onAny));
      src.onerror = () => { src.close(); failures += 1; setStatus(navigator.onLine === false ? "offline" : "reconnecting"); if (failures >= 2) startPoll(); retry = setTimeout(connect, Math.min(30000, 1500 * 2 ** Math.min(failures, 4)) + Math.random() * 500); };
    };
    connect();
    const onVis = () => { if (document.visibilityState === "hidden") hiddenAt.current = Date.now(); else { if (es.current?.readyState === 2) { clearTimeout(retry); connect(); } else if (Date.now() - hiddenAt.current > STALE_WAKE_MS) reconcile(); } };
    const onOnline = () => { clearTimeout(retry); es.current?.close(); connect(); }; const onOffline = () => setStatus("offline");
    document.addEventListener("visibilitychange", onVis); window.addEventListener("online", onOnline); window.addEventListener("offline", onOffline);
    return () => { closed = true; clearTimeout(retry); stopPoll(); es.current?.close(); everOpen.current = false; lastId.current = null; document.removeEventListener("visibilitychange", onVis); window.removeEventListener("online", onOnline); window.removeEventListener("offline", onOffline); };
  }, [ctxKey]); // eslint-disable-line

  const subscribe = (topics, cb) => { const id = Math.random().toString(36).slice(2); subs.current.set(id, { topics: new Set(topics), cb, pending: {} }); return () => { subs.current.delete(id); Object.keys(timers.current).filter((k) => k.startsWith(`${id}:`)).forEach((k) => { clearTimeout(timers.current[k]); delete timers.current[k]; }); }; };
  return <LiveCtx.Provider value={{ status, subscribe, reconcile }}>{children}</LiveCtx.Provider>;
}

/** Subscribe to permission-aware server events. onEvent(topic, data); onEvent("poll") = reconcile/refetch. Same API as before, now on the shared tab connection. */
export function useLiveEvents(topics, onEvent, enabled = true) {
  const ctx = useContext(LiveCtx); const cb = useRef(onEvent); cb.current = onEvent;
  const key = (topics || []).join(",");
  useEffect(() => {
    if (!enabled) return undefined;
    if (!ctx) { const t = setInterval(() => cb.current("poll", {}), POLL_MS); return () => clearInterval(t); }
    return ctx.subscribe(topics || [], (t, d) => cb.current(t, d));
  }, [enabled, key, ctx]); // eslint-disable-line
}

/** Topics a data path depends on (used by shared useLoad helpers so list/detail screens refetch automatically). Settings/config paths are excluded. */
export function topicsForPath(path = "") {
  const p = String(path || "");
  if (!p || /settings|policies|templates|preferences|specialties|plans$|branches|providers|permissions/.test(p)) return [];
  const t = [];
  if (/\/consult|\/appointments|\/queue|\/desk\/(summary|queue|appointments|patients)/.test(p)) t.push("appointment", "queue");
  if (/\/pharmacy|\/medicine|\/orders/.test(p)) t.push("order", "inventory", "prescription", "delivery");
  if (/\/desk|\/billing|\/refund|\/payments|\/finance|\/invoice|\/settlement|\/ledger/.test(p)) t.push("payment", "finance");
  if (/\/lab|\/diagnostic|\/report|\/radiology/.test(p)) t.push("lab", "diagnostics", "report");
  if (/\/home-collection|\/collection|\/phlebo/.test(p)) t.push("collection");
  if (/\/physio/.test(p)) t.push("physio");
  if (/\/corporate/.test(p)) t.push("corporate");
  if (/\/membership/.test(p)) t.push("membership");
  if (/\/notification|\/inbox|\/followups|\/referrals|\/leaves/.test(p)) t.push("notification", "referral", "appointment");
  if (/\/doctors|\/doctor\/me|\/applications/.test(p)) t.push("doctor");
  if (/\/enquir|\/support|\/callback/.test(p)) t.push("enquiry", "support");
  if (/\/admin\/(users|staff|access|roles|patient-db)/.test(p)) t.push("access", "membership");
  return [...new Set(t)];
}

export const useLiveStatus = () => useContext(LiveCtx)?.status || "offline";

/** Watch one record while a form is open: returns true once someone else changed it (caller shows a notice; never auto-overwrites the draft). */
export function useRecordWatch(topic, recordId, active = true) {
  const [changed, setChanged] = useState(false);
  useEffect(() => { setChanged(false); }, [recordId]);
  useLiveEvents([topic], (t, d) => { if (t !== "poll" && recordId && (d?.ids || []).includes(recordId)) setChanged(true); }, active && !!recordId);
  return [changed, () => setChanged(false)];
}

/** Subtle connection pill — only visible when updates may be stale. */
export function LiveIndicator() {
  const status = useLiveStatus(); const [show, setShow] = useState(false);
  useEffect(() => { if (status === "live") { setShow(false); return undefined; } const t = setTimeout(() => setShow(true), 4000); return () => clearTimeout(t); }, [status]);
  if (!show) return null;
  return <div data-testid="live-indicator" data-status={status} className={`fixed bottom-20 xl:bottom-4 left-3 z-[60] px-3 py-1.5 rounded-full text-[11px] font-bold shadow-lg border ${status === "offline" ? "bg-rose-50 text-rose-800 border-rose-200" : "bg-amber-50 text-amber-900 border-amber-200"}`}>
    {status === "offline" ? "Offline — showing last loaded data" : "Reconnecting live updates…"}</div>;
}
